import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import "../styles/Login.css";
import cardexLogo from "../assets/Cardex Login Final Logo.png";
import { login as apiLogin } from "../api/auth";
import { setToken, setRefreshToken, setUser, getRememberMe, setRememberMe, getSavedEmail, setSavedEmail } from "../auth/auth";

export default function Login() {
  const [email, setEmail] = useState(getSavedEmail());
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMeState] = useState(getRememberMe());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e: any) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (rememberMe) {
        setSavedEmail(email);
        setRememberMe(true);
      } else {
        setRememberMe(false);
      }
      
      const data = await apiLogin(email, password);
      if (!data?.token) {
        throw new Error("Missing auth token");
      }
      setToken(data.token);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      setUser(
        data.user || {
          name: "Cardex User",
          email,
          source: "email",
        }
      );
      
      // Check for return URL from guest gate
      const returnUrl = localStorage.getItem("returnUrl");
      if (returnUrl) {
        localStorage.removeItem("returnUrl");
        navigate(returnUrl);
      } else {
        navigate("/home");
      }
    } catch (_err: unknown) {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="screen login-screen">
      <div className="bg-glow"></div>
      <div className="login-grid-lines" aria-hidden="true"></div>
      <div className="login-noise" aria-hidden="true"></div>

      <div className="login-scene">
        <section className="hero login-hero">
          <img src={cardexLogo} alt="Cardex" className="logo" />
          <h1 className="title">Into The Market.</h1>
          <p className="subtitle">Bounties. Trades. Collecting.</p>
        </section>

        <section className="card login-panel" aria-label="Login form">
          <div className="login-panel-glow" aria-hidden="true"></div>
          <div className="login-panel-top">
            <div>
              <p className="login-panel-kicker">Sign in</p>
              <h2 className="login-panel-title">Welcome back</h2>
            </div>
          </div>

          <form className="form" onSubmit={handleLogin}>
            {error && <div className="error-msg">{error}</div>}

            <label className="label" htmlFor="login-email">Email Address</label>
            <input
              id="login-email"
              type="email"
              placeholder="collector@cardex.com"
              className="input"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              required
            />

            <label className="label" htmlFor="login-password">Password</label>
            <div className="password-wrap">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="input"
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
              </button>
            </div>

            <div className="remember-forgot-row">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e: any) => setRememberMeState(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <Link className="forgot" to="/forgot">
                Forgot password?
              </Link>
            </div>

            <button className="btn-primary login-submit" type="submit" disabled={loading}>
              <span>{loading ? "Logging in..." : "Enter Cardex"}</span>
            </button>

            <p className="legal-notice">
              By continuing, you agree to the Cardex{" "}
              <Link to="/terms" className="legal-link">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="legal-link">Privacy Policy</Link>.
            </p>

            <div className="divider">
              <span></span>
              <p>or</p>
              <span></span>
            </div>

            <p className="oauth-coming">More sign-in options are on the way.</p>
          </form>

          <footer className="footer login-footer">
            <p>New here?</p>
            <Link className="footer-cta" to="/signup">Create an account</Link>
          </footer>
        </section>
      </div>
    </main>
  );
}
